<script setup lang="ts">
import { useRouter } from 'vue-router'
import { api } from '../api/client'
import PageBody from '../components/PageBody.vue'
import PageHeader from '../components/PageHeader.vue'
import { Table, Td, Th, Tr } from '../components/Table'
import { Button, EmptyState, ErrorState, Panel, StatCard } from '../components/ui'
import { actionName, relativeTime, shortID } from '../lib/format'
import { useAsync, useTitle } from '../lib/hooks'

const router = useRouter()
useTitle('概览')

const { data, error, initial, reload } = useAsync(async () => {
  const [users, shows, audit] = await Promise.all([api.listUsers('', 1, 0), api.listShows(), api.listAudit(8, 0)])
  const list = shows.shows
  return {
    userTotal: users.total,
    showTotal: list.length,
    episodeTotal: list.reduce((sum, s) => sum + s.episode_count, 0),
    subtitleTotal: list.reduce((sum, s) => sum + s.subtitle_count, 0),
    topShows: [...list].sort((a, b) => b.subtitle_count - a.subtitle_count).slice(0, 6),
    recent: audit.entries,
  }
})
</script>

<template>
  <PageHeader title="概览">
    <template #actions>
      <Button size="sm" @click="router.push('/shows')">剧</Button>
      <Button size="sm" variant="primary" @click="router.push('/users')">账号</Button>
    </template>
  </PageHeader>

  <PageBody>
    <Panel v-if="error">
      <ErrorState :message="error.message" @retry="reload" />
    </Panel>
    <div v-else-if="initial" class="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      <div v-for="i in 4" :key="i" class="panel h-[86px] animate-pulse" />
    </div>
    <template v-else-if="data">
      <div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="账号" :value="data.userTotal" />
        <StatCard label="剧" :value="data.showTotal" />
        <StatCard label="集" :value="data.episodeTotal" tone="accent" />
        <StatCard label="字幕" :value="data.subtitleTotal" />
      </div>

      <div class="mt-4 grid gap-4 lg:grid-cols-2">
        <Panel title="剧">
          <EmptyState v-if="data.topShows.length === 0" title="还没有剧">
            <template #action>
              <Button size="sm" @click="router.push('/shows')">去导入</Button>
            </template>
          </EmptyState>
          <Table v-else>
            <template #head>
              <Tr>
                <Th class="w-[88px]">ID</Th>
                <Th class="w-[88px]">集</Th>
                <Th class="w-[88px]">字幕</Th>
              </Tr>
            </template>
            <Tr
              v-for="s in data.topShows"
              :key="s.id"
              clickable
              @click="router.push(`/shows/${s.id}`)"
            >
              <Td mono class="font-medium">#{{ s.id }}</Td>
              <Td mono>{{ s.episode_count }}</Td>
              <Td mono>{{ s.subtitle_count }}</Td>
            </Tr>
          </Table>
        </Panel>

        <Panel title="最近操作">
          <EmptyState v-if="data.recent.length === 0" title="暂无记录" />
          <ul v-else class="divide-y divide-line">
            <li
              v-for="e in data.recent"
              :key="e.id"
              class="flex items-start justify-between gap-3 px-4 py-2.5 text-[12.5px]"
            >
              <span class="min-w-0">
                <span class="text-text">{{ actionName(e.action) }}</span>
                <span class="mt-0.5 block truncate text-dim">
                  {{ e.actor_email || shortID(e.actor_id ?? '') }} · {{ e.target_type }} {{ e.target_id }}
                </span>
              </span>
              <span class="shrink-0 text-dim">{{ relativeTime(e.created_at) }}</span>
            </li>
          </ul>
        </Panel>
      </div>
    </template>
  </PageBody>
</template>
